import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Badge,
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  List,
  Modal,
  Paper,
  ScrollArea,
  Space,
  Text,
  Title
} from '@mantine/core';
import {
  IconBrandWhatsapp,
  IconCheck,
  IconCircleNumber1Filled,
  IconCircleNumber2Filled,
  IconCircleNumber3Filled,
  IconCircleNumber4Filled,
  IconExternalLink,
  IconInfoCircle
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { WhatsAppAccountCard } from 'components/WhatsAppAccountHandler/WhatsAppAccountCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { config } from 'environment/config';
import { WhatsAppIcon } from 'icons/WhatsAppIcon';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { WhatsAppService } from 'services/whatsapp-service';
import { WHATSAPP_ACCOUNTS_QUERY_KEY } from 'shared/constants/query-keys';
import {
  FacebookLoginResponse,
  FinishData,
  SessionInfo,
  SessionInfoEvent,
  SessionInfoType
} from 'shared/interfaces/FacebookSDK';
import { WhatsAppAccount } from 'shared/interfaces/WhatsAppAccount';

const WHATS_APP_MANAGE_LINK = 'https://business.facebook.com/latest/whatsapp_manager/overview';

type WhatsAppAccountHandlerProps = BoxProps;

export function WhatsAppAccountHandler({ ...boxProps }: WhatsAppAccountHandlerProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();
  const [connectAccountIntrusctionsOpened, setConnectAccountIntrusctionsOpened] = useState(false);
  const [registrationFailedOpen, setRegistrationFailedOpen] = useState(false);
  const embeddedSignupFinishDataRef = useRef<FinishData | undefined>(undefined);

  const whatsAppAccountsQuery = useQuery({
    queryKey: [WHATSAPP_ACCOUNTS_QUERY_KEY],
    queryFn: WhatsAppService.getAllRefreshedAccounts,
    enabled: subscription.product.enableWhatsApp || false
  });

  const connectWhatsAppAccountMutation = useMutation({
    mutationFn: WhatsAppService.connectAccount,
    onSuccess: (account: WhatsAppAccount) => {
      queryClient.setQueryData([WHATSAPP_ACCOUNTS_QUERY_KEY], (existingAccounts: WhatsAppAccount[] = []) => {
        const existingAccountIndex = existingAccounts.findIndex(
          (existingAccount) => existingAccount._id === account._id
        );
        if (existingAccountIndex !== -1) {
          const updatedAccounts = [...existingAccounts];
          updatedAccounts[existingAccountIndex] = account;
          return updatedAccounts;
        } else {
          return [...existingAccounts, account];
        }
      });
      NotificationService.showResult(true, 'FEED_BACK.WHATSAPP.SUCCESS.CONNECT');
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.Conflict) {
        NotificationService.showResult(false, 'FEED_BACK.WHATSAPP.FAILURE.CONFLICT');
      } else if (error.response?.status === HttpStatusCode.UnprocessableEntity) {
        setRegistrationFailedOpen(true);
      } else {
        NotificationService.showResult(false, 'FEED_BACK.WHATSAPP.FAILURE.CONNECT');
      }
    }
  });

  const signUpSessionInfoListener = (event: MessageEvent) => {
    if (!event.origin || !event.origin.endsWith('facebook.com')) return;

    try {
      const data = JSON.parse(event.data) as SessionInfo;
      if (data.type === SessionInfoType.WA_EMBEDDED_SIGNUP) {
        if (data.event === SessionInfoEvent.FINISH) embeddedSignupFinishDataRef.current = data.data as FinishData;
        else embeddedSignupFinishDataRef.current = undefined;
        // We could handle cancel and error SessionInfoType here, but we don't need to
      }
    } catch {
      embeddedSignupFinishDataRef.current = undefined;
    }
  };

  useEffect(() => {
    window.addEventListener('message', signUpSessionInfoListener);
    return () => {
      window.removeEventListener('message', signUpSessionInfoListener);
    };
  }, [signUpSessionInfoListener]);

  const launchWhatsAppSignup = () => {
    if (!window.FB) return;
    window.FB.login(
      (response: FacebookLoginResponse) => {
        if (response.authResponse?.code && embeddedSignupFinishDataRef.current) {
          const { waba_id, phone_number_id } = embeddedSignupFinishDataRef.current;
          connectWhatsAppAccountMutation.mutate({
            code: response.authResponse.code,
            whatsAppAccountId: waba_id,
            whatsAppPhoneNumberId: phone_number_id
          });
        } else {
          embeddedSignupFinishDataRef.current = undefined;
          NotificationService.showResult(false, 'FEED_BACK.WHATSAPP.FAILURE.CONNECT');
        }
      },
      {
        config_id: config.whatsAppEmbeddedSignupConfigId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          feature: 'whatsapp_embedded_signup',
          sessionInfoVersion: 3
        }
      }
    );
  };

  if (whatsAppAccountsQuery.isError) return <ErrorAlert h={boxProps.h} />;
  if (whatsAppAccountsQuery.isInitialLoading) return <FullScreenLoader h={boxProps.h} />;
  const whatsAppAccounts = whatsAppAccountsQuery.data || [];

  const CreateButton = ({ ...buttonProps }: ButtonProps) => (
    <Button
      variant="default"
      leftIcon={<WhatsAppIcon w={18} h={18} />}
      loading={connectWhatsAppAccountMutation.isLoading}
      onClick={() => setConnectAccountIntrusctionsOpened(true)}
      {...buttonProps}>
      {t('WHATSAPP.CONNECT_WHATSAPP_ACCOUNT')}
    </Button>
  );

  return (
    <>
      <UpgradePlanBlurCard message={t('WHATSAPP.UPGRADE.DESCRIPTION')} showCard={!subscription.product.enableWhatsApp}>
        <Box {...boxProps}>
          <Flex justify="space-between" align="center">
            <Flex direction="row" align="center">
              <Title order={5}>{t('WHATSAPP.CONNECTED_ACCOUNT')}</Title>
              <Space w={10} />
              <Badge px={4} mt={3} size="xs" color="gray">
                {whatsAppAccounts.length}
              </Badge>
            </Flex>
            {whatsAppAccounts.length > 0 && <CreateButton variant="default" />}
          </Flex>
          <ScrollArea w="100%" h="100%" mt={10}>
            {whatsAppAccounts.map((account, idx) => (
              <WhatsAppAccountCard
                key={idx}
                account={account}
                p={5}
                mt={idx === 0 ? 0 : 5}
                mb={idx === whatsAppAccounts.length - 1 ? 0 : 5}
                px={10}
              />
            ))}
            {whatsAppAccounts.length == 0 && subscription.product.enableWhatsApp && (
              <NoDataFound
                h="100%"
                size="xs"
                icon={<IconBrandWhatsapp />}
                title={t('NO_DATA_FOUND.WHATSAPP.TITLE')}
                subTitle={t('NO_DATA_FOUND.WHATSAPP.SUB_TITLE')}
                button={<CreateButton size="xs" />}
              />
            )}
          </ScrollArea>
        </Box>
      </UpgradePlanBlurCard>
      <Modal
        opened={connectAccountIntrusctionsOpened}
        onClose={() => setConnectAccountIntrusctionsOpened(false)}
        title={t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.TITLE')}
        size="lg"
        centered>
        <Alert
          mb="xs"
          icon={<IconInfoCircle />}
          title={t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.INFO.TITLE')}
          color="blue">
          <Text fz="sm">{t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.INFO.DESCRIPTION')}</Text>
          <Space h="md" />
          <Text fz="sm">{t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.DELETE_ACCOUNT.TITLE')}</Text>
          <List size="sm" mt="xs" ml="md" type="ordered">
            <List.Item icon={<IconCircleNumber1Filled />}>
              {t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.DELETE_ACCOUNT.STEP_1')}
            </List.Item>
            <List.Item icon={<IconCircleNumber2Filled />}>
              {t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.DELETE_ACCOUNT.STEP_2')}
            </List.Item>
            <List.Item icon={<IconCircleNumber3Filled />}>
              {t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.DELETE_ACCOUNT.STEP_3')}
            </List.Item>
            <List.Item icon={<IconCircleNumber4Filled />}>
              {t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.DELETE_ACCOUNT.STEP_4')}
            </List.Item>
          </List>
        </Alert>
        <Flex justify="flex-end">
          <Button
            mt="md"
            variant="outline"
            leftIcon={<IconCheck />}
            onClick={() => {
              setConnectAccountIntrusctionsOpened(false);
              launchWhatsAppSignup();
            }}>
            {t('WHATSAPP.CONNECT_ACCOUNT_INSTRUCTIONS.CTA')}
          </Button>
        </Flex>
      </Modal>
      <Modal
        opened={registrationFailedOpen}
        onClose={() => setRegistrationFailedOpen(false)}
        title={t('WHATSAPP.REGISTRATION_FAILED.TITLE')}
        size="lg"
        centered>
        <Text fz="md" mb="md">
          {t('WHATSAPP.REGISTRATION_FAILED.SUB_TITLE')}
        </Text>
        <Paper bg="gray.1" p="md">
          <Text fz="md">{t('WHATSAPP.REGISTRATION_FAILED.INSTRUCTIONS')}</Text>
          <List size="md" mt="xs" ml="md" type="ordered">
            <List.Item icon={<IconCircleNumber1Filled />}>{t('WHATSAPP.REGISTRATION_FAILED.STEP_1')}</List.Item>
            <List.Item icon={<IconCircleNumber2Filled />}>{t('WHATSAPP.REGISTRATION_FAILED.STEP_2')}</List.Item>
            <List.Item icon={<IconCircleNumber3Filled />}>{t('WHATSAPP.REGISTRATION_FAILED.STEP_3')}</List.Item>
          </List>
        </Paper>
        <Flex justify="flex-end">
          <Button
            mt="md"
            variant="outline"
            component="a"
            href={WHATS_APP_MANAGE_LINK}
            target="_blank"
            rightIcon={<IconExternalLink />}>
            {t('WHATSAPP.REGISTRATION_FAILED.CTA')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
